import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

// #region MultiComboboxProps
export interface MultiComboboxProps<T extends Record<string, any>> {
    /** Currently selected key values. Empty = nothing selected (shows the placeholder). */
    values: Array<string | number>;
    /** Called with the new list of selected key values. */
    onChange: (values: Array<string | number>) => void;
    /** Source list of option objects. */
    options: T[];
    /** Key on each option whose value is shown to the user. */
    labelKey: keyof T;
    /** Key on each option whose value is reported through onChange. Defaults to 'id'. */
    valueKey?: keyof T;
    /** Trigger label when nothing is selected. */
    placeholder?: string;
    /** Placeholder text inside the search input. */
    searchPlaceholder?: string;
    disabled?: boolean;
    className?: string;
    triggerClassName?: string;
    ariaLabel?: string;
    /**
     * ASYNC MODE. Hands the search term to the parent instead of filtering
     * `options` locally. When set, `options` renders verbatim — the server already
     * matched, and filtering again would drop valid rows.
     */
    onSearchChange?: (query: string) => void;
    /** Async mode: current search term, owned by the parent. */
    searchValue?: string;
    /** Async mode: show a loading row while a request is in flight. */
    loading?: boolean;
    /** Async mode: true when more rows exist past the ones supplied. */
    hasMore?: boolean;
    /** Async mode: fetch the next page. */
    onLoadMore?: () => void;
    /**
     * Async mode: resolves a selected key to its label. REQUIRED in async mode —
     * selected values routinely fall outside the current result page, so their
     * chips cannot be labelled from `options`.
     */
    labelForValue?: (value: string | number) => string | undefined;
    /** Text shown when there are no rows. */
    emptyLabel?: string;
}

// #region MultiCombobox
/**
 * Searchable MULTI-select dropdown. Same UX as Combobox (in-list search,
 * keyboard nav, outside-click/escape dismiss) but toggles multiple values and
 * stays open while picking. An empty selection reads as "all".
 *
 * Async mode (`onSearchChange`) additionally renders selected chips from
 * `labelForValue`, so selections survive falling out of the current result page.
 */
export function MultiCombobox<T extends Record<string, any>>({
    values,
    onChange,
    options,
    labelKey,
    valueKey,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    disabled = false,
    className = '',
    triggerClassName,
    ariaLabel,
    onSearchChange,
    searchValue,
    loading = false,
    hasMore = false,
    onLoadMore,
    labelForValue,
    emptyLabel = 'No matches',
}: MultiComboboxProps<T>): JSX.Element {
    const vKey = (valueKey ?? ('id' as keyof T)) as keyof T;
    const isAsync = typeof onSearchChange === 'function';

    const [open, setOpen] = useState(false);
    const [internalQuery, setInternalQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const query = isAsync ? (searchValue ?? '') : internalQuery;
    const selectedSet = useMemo(() => new Set(values.map((v) => String(v))), [values]);

    type Row = { key: string; label: string };
    const rows = useMemo<Row[]>(() => {
        const q = query.trim().toLowerCase();
        const mapped = options.map((o) => ({ key: String(o[vKey]), label: String(o[labelKey] ?? '') }));
        return isAsync ? mapped : mapped.filter((r) => (q ? r.label.toLowerCase().includes(q) : true));
    }, [options, labelKey, vKey, query, isAsync]);

    // #region labelFor
    // Async selections are commonly outside the current page, so the cache-backed
    // resolver wins over an options lookup.
    const labelFor = (value: string | number): string | undefined => {
        if (isAsync && labelForValue) {
            const resolved = labelForValue(value);
            if (resolved) return resolved;
        }
        const hit = options.find((o) => String(o[vKey]) === String(value));
        return hit ? String(hit[labelKey] ?? '') : undefined;
    };

    const triggerLabel = useMemo(() => {
        if (values.length === 0) return placeholder;
        if (values.length === 1) return labelFor(values[0]) || '1 selected';
        return `${values.length} selected`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, options, labelKey, vKey, placeholder, isAsync, labelForValue]);

    // Close on outside click / escape.
    useEffect(() => {
        if (!open) return;
        const onPointer = (e: MouseEvent | TouchEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onPointer, true);
        document.addEventListener('touchstart', onPointer, true);
        document.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('mousedown', onPointer, true);
            document.removeEventListener('touchstart', onPointer, true);
            document.removeEventListener('keydown', onKey, true);
        };
    }, [open]);

    // Keep the active index inside the visible row range when filtering shrinks the list.
    useEffect(() => {
        if (activeIdx >= rows.length) setActiveIdx(Math.max(0, rows.length - 1));
    }, [rows.length, activeIdx]);

    // Resolve the raw value from the stringified key (number when it round-trips cleanly).
    const rowValueFromKey = (key: string): string | number => {
        const n = Number(key);
        return Number.isFinite(n) && String(n) === key ? n : key;
    };

    const setSearch = (next: string) => {
        if (onSearchChange) onSearchChange(next);
        else setInternalQuery(next);
        setActiveIdx(0);
    };

    const toggle = (row: Row) => {
        if (selectedSet.has(row.key)) {
            onChange(values.filter((v) => String(v) !== row.key));
        } else {
            onChange([...values, rowValueFromKey(row.key)]);
        }
        inputRef.current?.focus(); // stay open for further multi-select
    };

    const onTriggerClick = () => {
        if (disabled) return;
        setOpen((o) => !o);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActiveIdx((i) => Math.min(rows.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (rows[activeIdx]) toggle(rows[activeIdx]);
        }
    };

    // Mirrors the Catalyst Listbox trigger so it matches the other filter dropdowns.
    const baseTrigger =
        'flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-950/10 dark:border-white/10 bg-surface-raised dark:bg-white/5 px-3 py-1.5 text-sm text-gray-900 dark:text-white hover:border-zinc-950/20 dark:hover:border-white/20 disabled:opacity-50';

    // Chips for values not present in the current page, so a selection made under
    // one search term stays visible (and removable) under another.
    const offPageSelections = isAsync
        ? values.filter((v) => !rows.some((r) => r.key === String(v)))
        : [];

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={onTriggerClick}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                className={triggerClassName ?? baseTrigger}
            >
                <span className="truncate">{triggerLabel}</span>
                <ChevronUpDownIcon className="h-5 w-5 shrink-0 text-zinc-500 dark:text-zinc-400 sm:h-4 sm:w-4" />
            </button>

            {open && (
                <div className="absolute left-0 z-40 mt-1 w-full min-w-[14rem] max-w-[calc(100vw-16px)] overflow-hidden rounded-lg border border-gray-200 dark:border-stone-700 bg-surface-alt shadow-lg">
                    <div className="flex items-center gap-2 border-b border-gray-200 dark:border-stone-700 px-2 py-1.5">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={onSearchKeyDown}
                            placeholder={searchPlaceholder}
                            className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
                            aria-label={searchPlaceholder}
                            autoFocus
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => { setSearch(''); inputRef.current?.focus(); }}
                                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                                aria-label="Clear search"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    {values.length > 0 && (
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            className="w-full border-b border-gray-200 dark:border-stone-700 px-3 py-1.5 text-left text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            Clear selection ({values.length})
                        </button>
                    )}
                    {offPageSelections.length > 0 && (
                        <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-stone-700 px-2 py-1.5">
                            {offPageSelections.map((v) => (
                                <button
                                    key={String(v)}
                                    type="button"
                                    onClick={() => onChange(values.filter((x) => String(x) !== String(v)))}
                                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                                >
                                    <span className="max-w-[10rem] truncate">{labelFor(v) || `#${v}`}</span>
                                    <XMarkIcon className="h-3 w-3" />
                                </button>
                            ))}
                        </div>
                    )}
                    <ul role="listbox" aria-multiselectable="true" className="max-h-64 overflow-y-auto py-1">
                        {rows.map((row, idx) => {
                            const isSelected = selectedSet.has(row.key);
                            return (
                                <li
                                    key={row.key}
                                    role="option"
                                    aria-selected={isSelected}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    onClick={() => toggle(row)}
                                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-900 dark:text-white hover:bg-blue-500 hover:text-white"
                                >
                                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                                        {isSelected && <CheckIcon className="h-4 w-4" />}
                                    </span>
                                    <span className="truncate">{row.label}</span>
                                </li>
                            );
                        })}
                        {loading && (
                            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Searching…</li>
                        )}
                        {!loading && rows.length === 0 && (
                            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</li>
                        )}
                        {!loading && hasMore && onLoadMore && (
                            <li>
                                <button
                                    type="button"
                                    onClick={onLoadMore}
                                    className="w-full px-3 py-1.5 text-left text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                                >
                                    Load more
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
